import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendUp, TrendDown, Plus, Trash, DownloadSimple, X, PencilSimple, InstagramLogo, FacebookLogo, TiktokLogo, Eye, CursorClick, CurrencyEur, EnvelopeSimple, ArrowsClockwise } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtStamp } from '@/utils/dateFmt';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

function Stat({ label, value, prev, change, onClick }) {
  return (
    <div onClick={onClick} className={`bg-white border border-brand-border rounded-2xl p-4 ${onClick ? 'cursor-pointer hover:border-brand-green/30' : ''}`}>
      <p className="text-xs text-brand-muted uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-heading font-bold text-brand-text mt-1">{value}</p>
      {change !== undefined && change !== null && (
        <div className="flex items-center gap-1 mt-1">
          {change >= 0 ? <TrendUp size={12} className="text-brand-green" /> : <TrendDown size={12} className="text-red-500" />}
          <span className={`text-[11px] font-medium ${change >= 0 ? 'text-brand-green' : 'text-red-500'}`}>{change >= 0 ? '+' : ''}{change}%</span>
          <span className="text-[10px] text-brand-muted">vs mês anterior</span>
        </div>
      )}
    </div>
  );
}

export default function CeoMonthlyMetricsPage() {
  useRequireAdmin();
  const confirmDelete = useConfirmDelete();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [panel, setPanel] = useState(null);
  const [showInfoForm, setShowInfoForm] = useState(false);
  const [infoForm, setInfoForm] = useState({ date: new Date().toISOString().split('T')[0], channel: 'WhatsApp', converted: false, notes: '' });
  const [infoRequests, setInfoRequests] = useState([]);
  const [showSocialForm, setShowSocialForm] = useState(false);
  const [socialForm, setSocialForm] = useState({ followers_instagram: 0, followers_facebook: 0, followers_tiktok: 0, reach: 0, clicks: 0, ad_spend: 0, notes: '' });
  const [kitLeads, setKitLeads] = useState(null);
  const [kitSyncing, setKitSyncing] = useState(false);

  const fetchKitLeads = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/ceo/kit-leads-metrics`);
      setKitLeads(res.data);
    } catch {}
  }, []);
  useEffect(() => { fetchKitLeads(); }, [fetchKitLeads]);

  const syncKitLeads = async () => {
    setKitSyncing(true);
    try {
      const res = await axios.post(`${API}/api/admin/kit-leads/sync`);
      if (res.data?.status === 'ok') toast.success(`Sincronização concluída: ${res.data.total} leads`);
      else toast.error('Sincronização falhou ou foi ignorada (ver logs)');
      fetchKitLeads();
    } catch { toast.error('Erro ao sincronizar com o Kit'); }
    finally { setKitSyncing(false); }
  };

  const fetch = useCallback(async () => {
    try {
      const [mRes, iRes] = await Promise.all([
        axios.get(`${API}/api/ceo/monthly-metrics?month=${month}`),
        axios.get(`${API}/api/ceo/info-requests?month=${month}`),
      ]);
      setData(mRes.data); setInfoRequests(iRes.data);
      if (mRes.data?.social_metrics) setSocialForm(mRes.data.social_metrics);
    } catch {}
  }, [month]);
  useEffect(() => { fetch(); }, [fetch]);

  const saveInfo = async () => {
    try { await axios.post(`${API}/api/ceo/info-requests`, infoForm); toast.success('Pedido registado'); setShowInfoForm(false); fetch(); } catch { toast.error('Erro'); }
  };
  const deleteInfo = async (id) => {
    const ok = await confirmDelete({ description: 'Vai apagar este pedido de informação.' });
    if (!ok) return;
    await axios.delete(`${API}/api/ceo/info-requests/${id}`); fetch();
  };
  const saveSocial = async () => {
    try { await axios.put(`${API}/api/ceo/social-metrics/${month}`, socialForm); toast.success('Métricas de redes sociais guardadas'); setShowSocialForm(false); fetch(); } catch { toast.error('Erro ao guardar'); }
  };
  const exportCsv = () => { window.open(`${API}/api/ceo/monthly-metrics/csv?month=${month}`, '_blank'); };

  if (!data) return <div className="text-center py-12 text-brand-muted">A carregar...</div>;

  return (
    <div className="fade-in" data-testid="ceo-monthly-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading font-medium text-2xl text-brand-text">Métricas Mensais</h2>
          <p className="text-sm text-brand-muted mt-1">Análise detalhada por mês</p>
        </div>
        <div className="flex gap-2 items-center">
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="rounded-xl border-brand-border w-36" />
          <button onClick={exportCsv} className="p-2 border border-brand-border rounded-xl hover:bg-brand-bg" title="Exportar CSV"><DownloadSimple size={18} className="text-brand-green" /></button>
        </div>
      </div>

      {/* Main KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat label="Total consultas" value={data.total_consultations.value} change={data.total_consultations.change} onClick={() => setPanel({ title: 'Consultas do mês', items: data.details })} />
        <Stat label="1.as Consultas" value={data.first_consultations.value} change={data.first_consultations.change} onClick={() => setPanel({ title: 'Primeiras consultas', items: data.details.filter(d => d.is_first) })} />
        <Stat label="Novos clientes" value={data.new_clients.value} change={data.new_clients.change} />
        <Stat label="Clientes ativos" value={data.active_clients.value} />
      </div>

      {/* Cancelamentos/Remarcações */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div onClick={() => data.cancellations?.details?.length && setPanel({ title: 'Consultas canceladas', items: (data.cancellations.details || []).map(d => ({ patient_name: d.patient_name, date: d.date, modality: d.reason, program: d.estimated_loss ? `€${d.estimated_loss}` : '' })) })} className={`bg-white border border-brand-border rounded-2xl p-4 ${data.cancellations?.count ? 'cursor-pointer hover:border-brand-green/30' : ''}`} data-testid="metric-cancelled">
          <p className="text-xs text-brand-muted uppercase tracking-wider">Canceladas</p>
          <p className="text-2xl font-heading font-bold text-gray-600 mt-1">{data.cancellations?.count || 0}</p>
        </div>
        <div onClick={() => data.reschedules?.details?.length && setPanel({ title: 'Consultas remarcadas', items: (data.reschedules.details || []).map(d => ({ patient_name: d.patient_name, date: d.date, modality: d.reason })) })} className={`bg-white border border-brand-border rounded-2xl p-4 ${data.reschedules?.count ? 'cursor-pointer hover:border-brand-green/30' : ''}`} data-testid="metric-rescheduled">
          <p className="text-xs text-brand-muted uppercase tracking-wider">Remarcadas</p>
          <p className="text-2xl font-heading font-bold text-amber-600 mt-1">{data.reschedules?.count || 0}</p>
        </div>
        <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="metric-cancel-rate">
          <p className="text-xs text-brand-muted uppercase tracking-wider">Taxa cancelamento</p>
          <p className="text-2xl font-heading font-bold text-brand-text mt-1">{data.cancellations?.rate || 0}%</p>
        </div>
        <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="metric-lost-revenue">
          <p className="text-xs text-brand-muted uppercase tracking-wider">Receita perdida (est.)</p>
          <p className="text-2xl font-heading font-bold text-red-500 mt-1">€{Number(data.cancellations?.lost_revenue || 0).toFixed(2)}</p>
        </div>
      </div>

      {/* Modality + Programs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-brand-border rounded-2xl p-5">
          <h3 className="text-xs text-brand-muted uppercase tracking-wider font-medium mb-3">Modalidade</h3>
          <div className="flex gap-4 mb-4">
            <div className="flex-1 text-center bg-brand-bg/30 rounded-xl p-3">
              <p className="text-xl font-heading font-bold text-brand-text">{data.presencial}</p>
              <p className="text-[10px] text-brand-muted">Presencial</p>
            </div>
            <div className="flex-1 text-center bg-blue-50/50 rounded-xl p-3">
              <p className="text-xl font-heading font-bold text-blue-600">{data.online}</p>
              <p className="text-[10px] text-brand-muted">Online</p>
            </div>
          </div>
          <h3 className="text-xs text-brand-muted uppercase tracking-wider font-medium mb-3 mt-4">Origem das 1.as consultas</h3>
          {Object.keys(data.origins).length === 0 ? <p className="text-xs text-brand-placeholder">Sem dados.</p> : (
            <div className="space-y-1.5">
              {Object.entries(data.origins).sort((a, b) => b[1] - a[1]).map(([origin, count]) => (
                <div key={origin} className="flex items-center justify-between">
                  <span className="text-sm text-brand-text">{origin}</span>
                  <span className="text-sm font-semibold text-brand-text">{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-brand-border rounded-2xl p-5">
          <h3 className="text-xs text-brand-muted uppercase tracking-wider font-medium mb-3">Por programa</h3>
          {Object.keys(data.by_program).length === 0 ? <p className="text-xs text-brand-placeholder">Sem dados.</p> : (
            <div className="space-y-2">
              {Object.entries(data.by_program).sort((a, b) => b[1] - a[1]).map(([prog, count]) => (
                <div key={prog} className="flex items-center justify-between" onClick={() => setPanel({ title: prog, items: data.details.filter(d => d.program === prog) })}>
                  <span className="text-sm text-brand-text cursor-pointer hover:text-brand-green">{prog}</span>
                  <span className="text-sm font-semibold text-brand-text">{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info requests */}
      <div className="bg-white border border-brand-border rounded-2xl p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-xs text-brand-muted uppercase tracking-wider font-medium">Pedidos de informação</h3>
            <p className="text-[10px] text-brand-muted mt-0.5">{data.info_requests.total} total ({data.info_requests.converted} convertidos)</p>
          </div>
          <button onClick={() => { setInfoForm({ date: new Date().toISOString().split('T')[0], channel: 'WhatsApp', converted: false, notes: '' }); setShowInfoForm(true); }} className="bg-brand-green text-white rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1"><Plus size={12} /> Registar</button>
        </div>
        {Object.keys(data.info_requests.by_channel).length > 0 && (
          <div className="flex gap-3 mb-3">
            {Object.entries(data.info_requests.by_channel).map(([ch, count]) => (
              <div key={ch} className="bg-brand-bg/30 rounded-xl px-3 py-2 text-center flex-1">
                <p className="text-lg font-heading font-semibold text-brand-text">{count}</p>
                <p className="text-[9px] text-brand-muted">{ch}</p>
              </div>
            ))}
          </div>
        )}
        {infoRequests.length > 0 && (
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {infoRequests.map(ir => (
              <div key={ir.id} className="flex items-center justify-between py-1.5 px-2 bg-brand-bg/20 rounded-lg text-xs">
                <span className="text-brand-text">{fmtDatePt(ir.date)} · {ir.channel}</span>
                <div className="flex items-center gap-2">
                  {ir.converted && <span className="text-[9px] bg-brand-green/10 text-brand-green px-1.5 py-0.5 rounded-full">Convertido</span>}
                  <button onClick={() => deleteInfo(ir.id)} className="text-brand-placeholder hover:text-red-500"><Trash size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Leads do Kit (Newsletter) — lifetime, por tipo de serviço */}
      <div className="bg-white border border-brand-border rounded-2xl p-5 mb-6" data-testid="kit-leads-section">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-xs text-brand-muted uppercase tracking-wider font-medium flex items-center gap-1.5"><EnvelopeSimple size={13} /> Leads do Kit (Newsletter)</h3>
            <p className="text-[10px] text-brand-muted mt-0.5">
              {kitLeads?.total_leads ?? 0} leads no total (todos os tempos)
              {kitLeads?.last_sync?.last_run_at && <> · última sincronização: {fmtStamp(kitLeads.last_sync.last_run_at)}</>}
            </p>
          </div>
          <button onClick={syncKitLeads} disabled={kitSyncing} className="bg-brand-green text-white rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1 disabled:opacity-50" data-testid="kit-leads-sync-btn">
            <ArrowsClockwise size={12} className={kitSyncing ? 'animate-spin' : ''} /> {kitSyncing ? 'A sincronizar...' : 'Sincronizar agora'}
          </button>
        </div>
        {!kitLeads || kitLeads.total_leads === 0 ? (
          <p className="text-xs text-brand-placeholder">Sem leads sincronizadas ainda.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {kitLeads.by_service.filter(s => s.total > 0).map(s => (
              <div key={s.service_type} className="bg-brand-bg/30 rounded-xl p-3.5" data-testid={`kit-leads-card-${s.service_type}`}>
                <p className="text-sm font-semibold text-brand-text">{s.label}</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-xl font-heading font-bold text-brand-text">{s.total}</span>
                  <span className="text-[10px] text-brand-muted">leads</span>
                </div>
                <div className="flex items-center justify-between mt-2 text-[11px]">
                  <span className="text-brand-muted">Já agendou</span>
                  <span className="font-semibold text-brand-green">{s.pct_scheduled}% ({s.matched})</span>
                </div>
                {s.avg_days_to_schedule !== null && (
                  <div className="flex items-center justify-between mt-1 text-[11px]">
                    <span className="text-brand-muted">Média dias até agendar</span>
                    <span className="font-semibold text-brand-text">{s.avg_days_to_schedule}d</span>
                  </div>
                )}
                {Object.values(s.distribution).some(v => v > 0) && (
                  <div className="flex gap-1 mt-2">
                    {Object.entries(s.distribution).map(([bucket, count]) => (
                      <div key={bucket} className="flex-1 text-center">
                        <div className="bg-brand-green/15 rounded h-8 flex items-end justify-center">
                          <div className="bg-brand-green rounded w-full" style={{ height: `${Math.max(8, (count / Math.max(...Object.values(s.distribution))) * 100)}%` }} />
                        </div>
                        <p className="text-[8px] text-brand-muted mt-0.5">{bucket}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Métricas de redes sociais (manual, mensal) */}
      <div className="bg-white border border-brand-border rounded-2xl p-5 mb-6" data-testid="social-metrics-section">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-xs text-brand-muted uppercase tracking-wider font-medium">Redes Sociais &amp; Anúncios</h3>
            <p className="text-[10px] text-brand-muted mt-0.5">Registo manual mensal</p>
          </div>
          <button onClick={() => setShowSocialForm(true)} className="bg-brand-green text-white rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1" data-testid="edit-social-metrics-btn"><PencilSimple size={12} /> Editar</button>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <div className="bg-brand-bg/30 rounded-xl p-2.5 text-center" data-testid="social-followers-instagram"><InstagramLogo size={14} className="mx-auto text-brand-green mb-1" weight="duotone" /><p className="text-sm font-heading font-semibold text-brand-text">{Math.round(data.social_metrics?.followers_instagram || 0)}</p><p className="text-[9px] text-brand-muted">Instagram</p></div>
          <div className="bg-brand-bg/30 rounded-xl p-2.5 text-center" data-testid="social-followers-facebook"><FacebookLogo size={14} className="mx-auto text-brand-green mb-1" weight="duotone" /><p className="text-sm font-heading font-semibold text-brand-text">{Math.round(data.social_metrics?.followers_facebook || 0)}</p><p className="text-[9px] text-brand-muted">Facebook</p></div>
          <div className="bg-brand-bg/30 rounded-xl p-2.5 text-center" data-testid="social-followers-tiktok"><TiktokLogo size={14} className="mx-auto text-brand-green mb-1" weight="duotone" /><p className="text-sm font-heading font-semibold text-brand-text">{Math.round(data.social_metrics?.followers_tiktok || 0)}</p><p className="text-[9px] text-brand-muted">TikTok</p></div>
          <div className="bg-brand-bg/30 rounded-xl p-2.5 text-center" data-testid="social-reach"><Eye size={14} className="mx-auto text-brand-green mb-1" weight="duotone" /><p className="text-sm font-heading font-semibold text-brand-text">{Math.round(data.social_metrics?.reach || 0)}</p><p className="text-[9px] text-brand-muted">Alcance</p></div>
          <div className="bg-brand-bg/30 rounded-xl p-2.5 text-center" data-testid="social-clicks"><CursorClick size={14} className="mx-auto text-brand-green mb-1" weight="duotone" /><p className="text-sm font-heading font-semibold text-brand-text">{Math.round(data.social_metrics?.clicks || 0)}</p><p className="text-[9px] text-brand-muted">Cliques</p></div>
          <div className="bg-brand-bg/30 rounded-xl p-2.5 text-center" data-testid="social-ad-spend"><CurrencyEur size={14} className="mx-auto text-brand-green mb-1" weight="duotone" /><p className="text-sm font-heading font-semibold text-brand-text">{Number(data.social_metrics?.ad_spend || 0).toFixed(2)}€</p><p className="text-[9px] text-brand-muted">Gasto em anúncios</p></div>
        </div>
        {data.social_metrics?.notes && <p className="text-[11px] text-brand-muted mt-3 italic">{data.social_metrics.notes}</p>}
      </div>

      {/* Detail panel */}
      {panel && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setPanel(null)}>
          <div className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-brand-border flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="font-heading font-medium text-lg text-brand-text">{panel.title}</h3>
              <button onClick={() => setPanel(null)} className="p-1.5 text-brand-muted hover:text-brand-text"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-2">
              {(panel.items || []).map((item, i) => (
                <div key={i} className="bg-brand-bg/30 border border-brand-border rounded-xl p-3">
                  <p className="text-sm font-medium text-brand-text">{item.patient_name}</p>
                  <div className="flex gap-2 mt-0.5 text-[10px] text-brand-muted">
                    <span>{fmtDatePt(item.date)}</span>
                    {item.modality && <span>{item.modality}</span>}
                    {item.program && <span>{item.program}</span>}
                    {item.is_first && <span className="text-amber-600">1.ª Consulta</span>}
                    {item.origin && <span className="text-purple-600">{item.origin}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Info request form */}
      <Dialog open={showInfoForm} onOpenChange={setShowInfoForm}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Registar pedido de informação</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Data</Label><Input type="date" value={infoForm.date} onChange={e => setInfoForm({ ...infoForm, date: e.target.value })} className="rounded-xl border-brand-border" /></div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Canal</Label>
              <div className="flex gap-1.5">
                {['WhatsApp', 'Formulário', 'Redes Sociais'].map(ch => (
                  <button key={ch} type="button" onClick={() => setInfoForm({ ...infoForm, channel: ch })} className={`flex-1 py-2 rounded-xl text-xs font-medium ${infoForm.channel === ch ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{ch}</button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={infoForm.converted} onChange={e => setInfoForm({ ...infoForm, converted: e.target.checked })} className="rounded border-brand-border w-4 h-4" />
              <span className="text-xs text-brand-text">Converteu em consulta</span>
            </label>
            <div><Label className="text-xs text-brand-muted mb-1 block">Notas (opcional)</Label><Input value={infoForm.notes} onChange={e => setInfoForm({ ...infoForm, notes: e.target.value })} placeholder="Detalhes..." className="rounded-xl border-brand-border" /></div>
            <button onClick={saveInfo} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">Registar</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Social metrics form */}
      <Dialog open={showSocialForm} onOpenChange={setShowSocialForm}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Redes Sociais &amp; Anúncios · {month}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs text-brand-muted mb-1 block">Seguidores IG</Label><Input type="number" value={socialForm.followers_instagram} onChange={e => setSocialForm({ ...socialForm, followers_instagram: e.target.value })} className="rounded-xl border-brand-border" data-testid="social-input-followers-instagram" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Seguidores FB</Label><Input type="number" value={socialForm.followers_facebook} onChange={e => setSocialForm({ ...socialForm, followers_facebook: e.target.value })} className="rounded-xl border-brand-border" data-testid="social-input-followers-facebook" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Seguidores TikTok</Label><Input type="number" value={socialForm.followers_tiktok} onChange={e => setSocialForm({ ...socialForm, followers_tiktok: e.target.value })} className="rounded-xl border-brand-border" data-testid="social-input-followers-tiktok" /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs text-brand-muted mb-1 block">Alcance</Label><Input type="number" value={socialForm.reach} onChange={e => setSocialForm({ ...socialForm, reach: e.target.value })} className="rounded-xl border-brand-border" data-testid="social-input-reach" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Cliques</Label><Input type="number" value={socialForm.clicks} onChange={e => setSocialForm({ ...socialForm, clicks: e.target.value })} className="rounded-xl border-brand-border" data-testid="social-input-clicks" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Gasto anúncios (€)</Label><Input type="number" step="0.01" value={socialForm.ad_spend} onChange={e => setSocialForm({ ...socialForm, ad_spend: e.target.value })} className="rounded-xl border-brand-border" data-testid="social-input-ad-spend" /></div>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Notas (opcional)</Label><Input value={socialForm.notes} onChange={e => setSocialForm({ ...socialForm, notes: e.target.value })} placeholder="Ex: campanha de lançamento Risotão" className="rounded-xl border-brand-border" data-testid="social-input-notes" /></div>
            <button onClick={saveSocial} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-social-metrics-btn">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
